/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { css, StyleSheet } from 'aphrodite';
import React from 'react';
import { useSelector } from 'react-redux';
import { NotaryAPI } from '../../api/notary/types';
import { usePermissions } from '../../hooks/usePermissions';
import { State } from '../../redux';
import { TaskCard } from './TaskCard';
import { RoomStatus } from '@shared/constants/room';
import { AttendAppointmentTask } from '@components/NotaryTaskList/AttendAppointmentTask';
import { getConfig } from '@utils/configs';

const styles = StyleSheet.create({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
});

export namespace NotaryTaskList {
  export interface Props {
    onReschedule: () => void;
    roomId: string;
    notaryId?: string;
    scheduleTime?: string;
  }
}

const openManageDocumentTab = (roomId: string) => window.open(`/notary/rooms/${roomId}/documents/manage`);

export const NotaryTaskList: React.FC<NotaryTaskList.Props> = ({ onReschedule, notaryId, roomId, scheduleTime }) => {
  const permissions = usePermissions();
  const user = useSelector((reduxState: State) => reduxState.user);
  const roomDetail = useSelector((reduxState: State) => reduxState.roomDetail);
  const cards = [];

  const manageClosingDocumentsCard = (
    <TaskCard
      key="manageDocs"
      id="manageDocs"
      taskName="Manage closing documents"
      buttonLabel="Manage"
      onOpenTask={() => openManageDocumentTab(roomId)}
    />
  );
  const isAllowedToManageDocs = roomDetail?.room?.status !== RoomStatus.DRAFT;
  if (isAllowedToManageDocs) {
    cards.push(manageClosingDocumentsCard);
  }

  const needsConfirmSchedule = roomDetail?.room?.status !== RoomStatus.PAUSED && (!notaryId || !scheduleTime);

  if (needsConfirmSchedule) {
    cards.push(
      <TaskCard
        key="schedule"
        id="schedule"
        taskName="Confirm Notarization Appointment"
        buttonLabel="Confirm"
        onOpenTask={onReschedule}
      />,
    );
  }

  const hasNotarizationPermission = permissions.includes(NotaryAPI.BSRPermissions.BSRNotarization);
  const roomStatusesForAttendAppointment = getConfig().allowNotaryToCorrectDoc
    ? [RoomStatus.CREATED, RoomStatus.NEEDS_ATTENTION, RoomStatus.COMPLETED]
    : [RoomStatus.CREATED];
  const isAllowedToAttendAppointment =
    hasNotarizationPermission &&
    roomDetail?.room?.notaryId === user?.id &&
    roomStatusesForAttendAppointment.includes(roomDetail?.room?.status);

  if (isAllowedToAttendAppointment) {
    cards.push(<AttendAppointmentTask key="attendAppointment" roomId={roomId} />);
  }

  return (
    <div data-it="notary-task-list" className={css(styles.list)} data-dd-privacy="allow">
      {cards}
    </div>
  );
};
